import { useEffect, useRef, type RefObject } from 'react'

/**
 * Close-on-outside-press for popups/overlays: while `enabled`, any pointerdown
 * whose composed path misses every ref fires `onOutside`. Detection uses
 * `composedPath()` rather than `ref.contains(event.target)` because the widget
 * renders inside a shadow DOM, where a document-level listener only sees the
 * retargeted host element. Pass the popup's root plus any toggle trigger —
 * excluding the trigger lets its own onClick handle the close (otherwise the
 * outside-press closes and the click immediately reopens).
 */
export function useOutsidePress(
  refs: RefObject<Element | null> | Array<RefObject<Element | null>>,
  onOutside: () => void,
  enabled = true,
) {
  // Read callback and refs through refs of their own so the document listener
  // binds/unbinds only when `enabled` flips, not on every caller render
  // (which happens per streamed token in the chat views).
  const onOutsideRef = useRef(onOutside)
  onOutsideRef.current = onOutside
  const refsRef = useRef(refs)
  refsRef.current = refs

  useEffect(() => {
    if (!enabled) return
    function onPointerDown(ev: PointerEvent) {
      const path = ev.composedPath()
      const list = Array.isArray(refsRef.current) ? refsRef.current : [refsRef.current]
      for (const r of list) {
        if (r.current && path.includes(r.current)) return
      }
      onOutsideRef.current()
    }
    // Capture phase — an inner stopPropagation() can't leave a stale popup open.
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [enabled])
}
